import React, { useState } from 'react';
import { Play, Download, TrendingDown } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/export';

const s = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto' },
  h1: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.75rem', marginBottom: 4 },
  desc: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: 700, lineHeight: 1.6 },
  btn: { fontFamily: 'DM Sans, sans-serif', background: 'var(--primary)', color: 'var(--bg)', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnOutline: { fontFamily: 'DM Sans, sans-serif', background: 'transparent', color: 'var(--secondary)', border: '1px solid var(--secondary)', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 },
  actions: { display: 'flex', gap: 12, marginBottom: '1.5rem', alignItems: 'center' },
  card: { background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Sans, sans-serif' },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.1)', color: 'var(--secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.05)', color: 'var(--text)', fontSize: '0.88rem' },
  spinner: { display: 'inline-block', width: 18, height: 18, border: '2px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  sortBtn: { background: 'none', border: 'none', color: 'var(--secondary)', cursor: 'pointer', fontSize: '0.7rem', marginLeft: 4 },
};

const fallbackData = [
  { name: 'Rachel W.', tier: 'Bloom', previousSpend: '$82/mo', currentSpend: '$48/mo', decline: -41, pattern: 'Dropped add-ons, coffee only', action: 'Re-introduce favorites bundle' },
  { name: 'James F.', tier: 'Harvest', previousSpend: '$145/mo', currentSpend: '$98/mo', decline: -32, pattern: 'Fewer weekend visits', action: 'Weekend loyalty bonus' },
  { name: 'Lisa B.', tier: 'Bloom', previousSpend: '$68/mo', currentSpend: '$52/mo', decline: -24, pattern: 'Switched from specialty to drip', action: 'Specialty drink BOGO' },
  { name: 'Tom H.', tier: 'Seedling', previousSpend: '$35/mo', currentSpend: '$22/mo', decline: -37, pattern: 'Visit frequency halved', action: 'Win-back push notification' },
  { name: 'Jordan R.', tier: 'Bloom', previousSpend: '$72/mo', currentSpend: '$55/mo', decline: -24, pattern: 'No food purchases', action: 'Free pastry with next visit' },
  { name: 'Priya N.', tier: 'Harvest', previousSpend: '$160/mo', currentSpend: '$120/mo', decline: -25, pattern: 'Alternating with competitor', action: 'Exclusive member perk reminder' },
  { name: 'Michael C.', tier: 'Seedling', previousSpend: '$28/mo', currentSpend: '$18/mo', decline: -36, pattern: 'Morning-only, skipping afternoons', action: 'Afternoon happy hour offer' },
  { name: 'Sarah M.', tier: 'Bloom', previousSpend: '$75/mo', currentSpend: '$58/mo', decline: -23, pattern: 'Smaller size orders', action: 'Size upgrade on the house' },
];

export default function DeclinerDetection() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('decline');
  const [sortDir, setSortDir] = useState('asc');

  const runModel = async () => {
    setLoading(true);
    try {
      const res = await api.post('/models/decliner');
      setResults(res.data?.results || fallbackData);
      toast.success('Decliner detection complete');
    } catch {
      setResults(fallbackData);
      toast.success('Decliner detection complete (demo data)');
    } finally {
      setLoading(false);
    }
  };

  const sorted = [...(results || [])].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
  const toggleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };

  return (
    <div style={s.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <h1 style={s.h1}>Decliner Detection</h1>
      <p style={s.desc}>
        Identifies customers with 20%+ spend decline over the trailing 60 days. Surfaces the behavioral pattern
        behind each decline and recommends targeted recovery actions.
      </p>

      <div style={s.actions}>
        <button style={s.btn} onClick={runModel} disabled={loading}>
          {loading ? <><span style={s.spinner} /> Running...</> : <><Play size={16} /> Run Model</>}
        </button>
        {results && (
          <button style={s.btnOutline} onClick={() => exportToCSV(results, 'decliner_detection')}>
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>

      {results && (
        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                {[['name','Customer'],['tier','Tier'],['previousSpend','Previous'],['currentSpend','Current'],['decline','Decline %'],['pattern','Pattern'],['action','Recommended Action']].map(([k,l]) => (
                  <th key={k} style={s.th}>{l}<button style={s.sortBtn} onClick={() => toggleSort(k)}>{sortKey === k ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={i}>
                  <td style={s.td}>{row.name}</td>
                  <td style={s.td}>{row.tier}</td>
                  <td style={s.td}>{row.previousSpend}</td>
                  <td style={s.td}>{row.currentSpend}</td>
                  <td style={{ ...s.td, color: 'var(--alert)', fontWeight: 600 }}>{row.decline}%</td>
                  <td style={s.td}>{row.pattern}</td>
                  <td style={s.td}>{row.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
